import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronRight, ChevronLeft, ArrowRight, Sparkles, RotateCcw, TrendingUp, Star, Heart, Tag } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { HeroBanner } from '../components/HeroBanner';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductRow = ({ title, icon: Icon, products, viewAllLink, viewAllLabel }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4 px-4 sm:px-0">
        <div className="flex items-center gap-2.5">
          <Icon className="w-5 h-5 text-[color:var(--color-primary)]" />
          <h2 
            className="section-title text-xl sm:text-2xl"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full bg-sand text-muted hover:text-white hover:bg-[color:var(--color-primary)] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full bg-sand text-muted hover:text-white hover:bg-[color:var(--color-primary)] transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {viewAllLink && (
            <Link 
              to={viewAllLink} 
              className="flex items-center gap-1 text-[color:var(--color-primary)] text-xs font-bold uppercase tracking-wider hover:underline ml-2"
            >
              {viewAllLabel || 'Voir tout'} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth no-scrollbar px-4 sm:px-0"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map(product => (
          <div key={product.id} className="flex-shrink-0 w-[160px] sm:w-[200px]">
            <ProductCard product={product} compact />
          </div>
        ))}
      </div>
    </section>
  );
};

const HomePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API_URL}/api/products/categories`)
      .then(r => setCategories((r.data || []).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchHomepage = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/homepage`);
        setData(response.data);
      } catch (error) {
        // Error shown via loading=false + null data check in UI
      } finally {
        setLoading(false);
      }
    };
    fetchHomepage();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogue?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="min-h-screen bg-main">
      {/* Hero Section */}
      <div className="relative pt-20 pb-8 sm:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10">
          <div className="bg-sand rounded-3xl px-6 sm:px-10 py-8 sm:py-12 relative overflow-hidden" data-testid="home-hero">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[color:var(--color-primary)] opacity-[0.07] pointer-events-none" />
            <div className="absolute right-24 -bottom-24 w-56 h-56 rounded-full bg-[color:var(--color-emerald)] opacity-[0.08] pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <p className="text-[color:var(--color-emerald)] text-xs font-bold uppercase tracking-[0.25em] mb-3">Wholesale · Parfumerie de qualité</p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl text-heading font-bold mb-3 leading-[1.05]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Ô'<span className="text-[color:var(--color-gold)]">Delicieuse</span>
              </h1>
              <p className="text-body text-sm sm:text-base mb-6 max-w-lg">
                {t('home_browse_catalogue')} — {t('nav_catalogue').toLowerCase()} détail, semi-gros et gros, stock en direct.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="max-w-xl mb-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                    <input
                      type="text"
                      placeholder={t('home_search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full input-field pl-12 pr-4 py-3.5 text-base rounded-full shadow-sm"
                      data-testid="home-search-bar"
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-primary px-5 sm:px-6 py-3.5 flex items-center gap-2 flex-shrink-0 rounded-full"
                    data-testid="home-search-btn"
                  >
                    <Search className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('catalogue_search_btn')}</span>
                  </button>
                </div>
              </form>

              <div className="flex flex-wrap items-center gap-2" data-testid="home-category-chips">
                <Link to="/catalogue" className="chip chip-active" data-testid="browse-catalogue">
                  {t('home_browse_catalogue')}
                </Link>
                {categories.slice(0, 8).map(c => (
                  <Link key={c} to={`/catalogue?category=${encodeURIComponent(c)}`} className="chip" data-testid={`home-chip-${c}`}>
                    {c}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bannière défilante (gérée depuis l'admin) */}
      <HeroBanner />

      {/* Product Sections */}
      <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="space-y-10 px-4 sm:px-0">
            {[...Array(3)].map((_, i) => (
              <div key={`section-skeleton-${i}`}>
                <div className="h-6 bg-surface w-48 mb-4 animate-pulse" />
                <div className="flex gap-3 overflow-hidden">
                  {[...Array(5)].map((_, j) => (
                    <div key={`card-skeleton-${i}-${j}`} className="flex-shrink-0 w-[160px] sm:w-[200px]">
                      <div className="card-surface animate-pulse">
                        <div className="aspect-square bg-surface" />
                        <div className="p-3 space-y-2">
                          <div className="h-2 bg-surface w-16" />
                          <div className="h-3 bg-surface w-24" />
                          <div className="h-3 bg-surface w-12" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : data ? (
          <>
            {data.promos?.length > 0 && (
              <ProductRow 
                title={t('home_promos')} 
                icon={Tag}
                products={data.promos} 
                viewAllLink="/catalogue?promo=1"
                viewAllLabel={t('home_view_all')}
              />
            )}
            <ProductRow 
              title={t('home_new_arrivals')} 
              icon={Sparkles}
              products={data.nouveautes} 
              viewAllLink="/catalogue"
              viewAllLabel={t('home_view_all')}
            />
            
            <ProductRow 
              title={t('home_back_in_stock')} 
              icon={RotateCcw}
              products={data.restocks} 
              viewAllLink="/catalogue"
              viewAllLabel={t('home_view_all')}
            />

            <ProductRow
              title={t('home_weekly_picks')}
              icon={Heart}
              products={data.coups_de_coeur}
              viewAllLink="/catalogue"
              viewAllLabel={t('home_view_all')}
            />
            
            <ProductRow 
              title={t('home_bestsellers')} 
              icon={TrendingUp}
              products={data.bestsellers} 
              viewAllLink="/catalogue"
              viewAllLabel={t('home_view_all')}
            />
            
            <ProductRow 
              title={t('home_selection')} 
              icon={Star}
              products={data.selection} 
              viewAllLink="/catalogue"
              viewAllLabel={t('home_view_all')}
            />
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted">{t('home_load_error')}</p>
            <button onClick={() => window.location.reload()} className="text-[color:var(--color-primary)] mt-2 hover:underline text-sm">
              {t('home_retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
